import { useEffect, useRef, useState, type HTMLAttributes, type ReactNode, type Ref } from 'react';
import clsx from 'clsx';
import { Button } from './Button.js';
import './ResultBanner.css';

/** How long the result bar waits before it continues by itself.
 *  Tornike, 2026-09-23: "count down for three seconds, then advance". */
export const RESULT_AUTO_ADVANCE_MS = 3000;

export type ResultState = 'neutral' | 'correct' | 'wrong';

export interface ResultBannerProps extends Omit<HTMLAttributes<HTMLDivElement>, 'children' | 'title'> {
  /**
   * - `neutral`: pre-check, shows only the CTA button.
   * - `correct`: user got it right, green banner.
   * - `wrong`: user got it wrong, red banner.
   */
  state: ResultState;
  /** Big bold line — "Correct!" / "Not quite". */
  title?: ReactNode;
  /** Detail line under the title (e.g. the correct answer if wrong). */
  detail?: ReactNode;
  /** Primary CTA label (defaults to Georgian depending on state). */
  ctaLabel?: ReactNode;
  onCta?: () => void;
  /** Optional secondary button (e.g. "Skip"). */
  secondaryLabel?: ReactNode;
  onSecondary?: () => void;
  /** Slide in from bottom on mount (default true). */
  animate?: boolean;
  /**
   * Continue on its own after this many ms, counting down on the button.
   * Only applies once a verdict is showing — a pre-check bar never advances
   * by itself. Tapping the button still wins immediately.
   */
  autoAdvanceMs?: number;
  ref?: Ref<HTMLDivElement>;
}

/**
 * ResultBanner — the sticky bottom strip. Three states:
 *   - neutral (pre-check)   → just a "Check" CTA
 *   - correct               → green banner + "Continue"
 *   - wrong                 → red banner + "Got it" (optionally shows the correct answer)
 * The CTA inside always uses the right variant (primary/secondary/neutral).
 */
export function ResultBanner({
  state,
  title,
  detail,
  ctaLabel,
  onCta,
  secondaryLabel,
  onSecondary,
  animate = true,
  autoAdvanceMs,
  className,
  ref,
  ...rest
}: ResultBannerProps) {
  // Auto-advance. The timer is armed per verdict: `state` returns to
  // 'neutral' between exercises, so each new result starts its own count.
  // onCta is read through a ref — its identity changes every render, and
  // re-arming the timer on that would keep the bar alive for ever.
  const [secondsLeft, setSecondsLeft] = useState<number | null>(null);
  const ctaRef = useRef(onCta);
  ctaRef.current = onCta;
  const armed = state !== 'neutral' && autoAdvanceMs != null && autoAdvanceMs > 0;
  useEffect(() => {
    if (!armed || autoAdvanceMs == null) {
      setSecondsLeft(null);
      return;
    }
    const startedAt = Date.now();
    const total = Math.ceil(autoAdvanceMs / 1000);
    setSecondsLeft(total);
    const tick = window.setInterval(() => {
      const left = Math.max(0, total - Math.floor((Date.now() - startedAt) / 1000));
      setSecondsLeft(left);
    }, 200);
    const fire = window.setTimeout(() => ctaRef.current?.(), autoAdvanceMs);
    return () => {
      window.clearInterval(tick);
      window.clearTimeout(fire);
    };
  }, [armed, autoAdvanceMs, state, title, detail]);

  const role = state === 'neutral' ? undefined : 'status';
  const ariaLive = state === 'neutral' ? undefined : 'polite';

  const defaultCta =
    state === 'neutral' ? 'შემოწმება' : state === 'correct' ? 'გააგრძელე' : 'გავიგე';

  // On correct/wrong the banner itself is saturated (green / red). The CTA
  // must contrast — neutral (cream) reads cleanly on both. Primary green
  // CTA only on the pre-check (white) banner.
  const ctaVariant = state === 'neutral' ? 'primary' : 'neutral';

  return (
    <div
      ref={ref}
      className={clsx('fluent-resultbar', className)}
      data-state={state}
      data-animate={animate || undefined}
      role={role}
      aria-live={ariaLive}
      {...rest}
    >
      {state !== 'neutral' && (
        <div className="fluent-resultbar__head">
          <span className="fluent-resultbar__icon" aria-hidden="true">
            {state === 'correct' ? <CheckGlyph /> : <CrossGlyph />}
          </span>
          {title != null && <h3 className="fluent-resultbar__title">{title}</h3>}
        </div>
      )}
      {detail != null && <p className="fluent-resultbar__detail">{detail}</p>}
      <div className="fluent-resultbar__cta">
        {secondaryLabel != null && (
          <Button variant="ghost" size="md" onClick={onSecondary}>
            {secondaryLabel}
          </Button>
        )}
        <Button variant={ctaVariant} size="md" onClick={onCta}>
          {ctaLabel ?? defaultCta}
          {secondsLeft != null && secondsLeft > 0 && (
            /* aria-hidden: the bar is a polite live region, and a number
               changing every second would be read out over and over. */
            <span className="fluent-resultbar__count" aria-hidden="true">
              {secondsLeft}
            </span>
          )}
        </Button>
      </div>
    </div>
  );
}

function CheckGlyph() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M5 12.5l4.5 4.5L19 7.5" />
    </svg>
  );
}
function CrossGlyph() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M6 6l12 12M18 6L6 18" />
    </svg>
  );
}
